import React, { useEffect, useState } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Box,
    CssBaseline,
    ListItemButton,
} from "@mui/material";
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Business as BusinessIcon,
    Group as GroupIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    BubbleChart,
    DocumentScanner,
    FavoriteRounded,
    RemoveRedEye,
    ContactMail,
    Map,
} from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import CustomSVG from "./CustomizeSvg";
import { useCountryParam } from "./util/useCountryParam";

interface AdminNavbarProps {
    children: React.ReactNode;
    darkTheme: boolean;
}
const AdminNavbar: React.FC<AdminNavbarProps> = ({ children, darkTheme }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const country = useCountryParam();

    // Menu items for India (limited set)
    const indiaMenuItems = [
        { text: "Dashboard", icon: <DashboardIcon />, route: "/dashboard" },
        { text: "Properties", icon: <BusinessIcon />, route: "/properties" },
        { text: "Arrange Viewing", icon: <RemoveRedEye />, route: "/arrangeView" },
        { text: "Contacts", icon: <ContactMail />, route: "/contacts" },
        { text: "Areas", icon: <Map />, route: "/areas" },
        { text: "Enquiry List", icon: <HelpOutlineIcon />, route: "/enquiry" },
        { text: "Users", icon: <GroupIcon />, route: "/users" },
        { text: "Settings", icon: <SettingsIcon />, route: "/settings" },
    ];

    // Menu items for UK (all items)
    const ukMenuItems = [
        { text: "Dashboard", icon: <DashboardIcon />, route: "/dashboard" },
        { text: "Properties", icon: <BusinessIcon />, route: "/properties" },
        { text: "Arrange Viewing", icon: <RemoveRedEye />, route: "/arrangeView" },
        { text: "Valuations", icon: <BubbleChart />, route: "/valuations" },
        { text: "Enquiry List", icon: <HelpOutlineIcon />, route: "/enquiry" },
        { text: "Jobs", icon: <DocumentScanner />, route: "/jobs" },
        { text: "Users", icon: <GroupIcon />, route: "/users" },
        { text: "WishList", icon: <FavoriteRounded />, route: "/wishlist" },
        { text: "Settings", icon: <SettingsIcon />, route: "/settings" },
    ];

    // Select menu items based on country
    const menuItems = country === 'india' ? indiaMenuItems : ukMenuItems;

    useEffect(() => {
        const user: any = sessionStorage.getItem("user");
        if (!user) {
            navigate("/auth/login"); // Redirect to login if no user is logged in
        }
        if (JSON.parse(user)?.role !== "admin") {
            navigate("/auth/login");
        }
    }, [navigate]);

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const getListItemStyles = (isActive: boolean) => ({
        backgroundColor: isActive ? (darkTheme ? "#1A374D" : "#10374A") : "transparent",
        color: isActive
            ? "#FFFFFF"
            : darkTheme
                ? "#CCCCCC" // Light text for dark theme
                : "#000000", // Dark text for light theme
        "&:hover": {
            backgroundColor: darkTheme ? "#1A374D" : "#10374A",
            color: "#FFFFFF",
            "& .MuiListItemIcon-root": {
                color: "#FFFFFF",
            },
        },
    });

    const getIconStyles = (isActive: boolean) => ({
        color: isActive
            ? "#FFFFFF"
            : darkTheme
                ? "#CCCCCC" // Light icons for dark theme
                : "#000000", // Dark icons for light theme
    });
    const logout = () => {
        sessionStorage.removeItem("user");
        navigate("/auth/login");
    };

    const handleNavigation = (route: string) => {
        // Preserve country parameter if it exists
        const country = searchParams.get('country');
        const fullRoute = country ? `${route}?country=${country}` : route;
        navigate(fullRoute);
        setDrawerOpen(false);
    };

    const handleLogoClick = () => {
        // Navigate to dashboard while preserving current country parameter
        const currentCountry = searchParams.get('country');
        const dashboardRoute = currentCountry ? `/dashboard?country=${currentCountry}` : '/dashboard';
        navigate(dashboardRoute);
    };

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />
            {/* AppBar */}
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, background: "linear-gradient(to right, #10374A, #1e6283)" }}>
                <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                    {/* Hamburger menu for mobile */}
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        sx={{ mr: 2, display: { sm: "none" } }}
                        onClick={handleDrawerToggle}
                    >
                        <MenuIcon />
                    </IconButton>
                    {/* Navbar Title */}
                    <Typography variant="h6" noWrap component="div" sx={{ lineHeight: 0.6, cursor: 'pointer' }} onClick={handleLogoClick}>
                        {/* <img
                            src={logo}
                            alt="Royale Space"
                            style={{
                                maxWidth: "140px",
                                height: "auto",
                            }}
                        /> */}
                        <CustomSVG />
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Drawer for Navigation */}
            <Drawer
                variant="temporary"
                open={drawerOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile
                }}
                sx={{
                    display: { xs: "block", sm: "none" },
                    "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
                }}
            >
                <List>
                    {menuItems.map((item, index) => (
                        <ListItem key={index}>
                            <ListItemButton onClick={() => handleNavigation(item.route)} sx={getListItemStyles(location.pathname === item.route)}>
                                <ListItemIcon sx={getIconStyles(location.pathname === item.route)}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    <ListItem>
                        <ListItemButton onClick={() => logout()} sx={getListItemStyles(location.pathname === "/auth/login")}>
                            <ListItemIcon sx={getIconStyles(location.pathname === "/auth/login")}>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText primary={"Logout"} />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Drawer>

            <Drawer
                variant="permanent"
                sx={{
                    width: 240,
                    flexShrink: 0,
                    display: { xs: "none", sm: "block" },
                    "& .MuiDrawer-paper": { width: 240, boxSizing: "border-box" },
                }}
            >
                <Toolbar />
                <List>
                    {menuItems.map((item, index) => (
                        <ListItem key={index}>
                            <ListItemButton onClick={() => handleNavigation(item.route)} sx={getListItemStyles(location.pathname === item.route)}>
                                <ListItemIcon sx={getIconStyles(location.pathname === item.route)}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    <ListItem>
                        <ListItemButton onClick={() => logout()} sx={getListItemStyles(location.pathname === "/auth/login")}>
                            <ListItemIcon sx={getIconStyles(location.pathname === "/auth/login")}>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText primary={"Logout"} />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Drawer>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    maxWidth: "100vw", // Prevents overflow
                    overflowX: "hidden", // Ensures no horizontal scrollbars
                }}
            >
                {children}
            </Box>
            <ToastContainer position="top-right" autoClose={3000} />
        </Box>
    );
};

export default AdminNavbar;
